import { ToastProvider, ToastViewport } from "@tamagui/toast";
import { UserRole, useAuth } from "common";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
} from "react-router";
import { Button, Text, YStack, TamaguiProvider } from "tamagui";
import tamaguiConfig from "tamagui-config";

import { CurrentToast } from "./components/CurrentToast";
import Layout from "./components/Layout";
import {
  UserRoleProvider,
  useUserRoleContext,
} from "./contexts/UserRoleContext";
import AlertDetails from "./pages/Alerts/AlertDetails";
import Alerts from "./pages/Alerts/Alerts";
import Analytics from "./pages/Analytics/Analytics";
import BaseStations from "./pages/BaseStations/BaseStations";
import CreateCustomer from "./pages/Customers/CreateCustomer";
import CustomerList from "./pages/Customers/CustomerList";
import { CustomerView } from "./pages/Customers/CustomerView";
import ForgotPassword from "./pages/ForgotPassword";
import Login from "./pages/Login";
import OAuthCallback from "./pages/OAuthCallback";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordConfirmation from "./pages/ResetPasswordConfirmation";
import SensorEvents from "./pages/SensorEvents";
import Sensors from "./pages/Sensors/Sensors";
import ServiceRequestDetail from "./pages/ServiceRequests/ServiceRequestDetail";
import ServiceRequestForm from "./pages/ServiceRequests/ServiceRequestForm";
import ServiceRequestsList from "./pages/ServiceRequests/ServiceRequestsList";
import Integrations from "./pages/Settings/Integrations";
import Notifications from "./pages/Settings/Notifications";
import Security from "./pages/Settings/Security";
import SensorManuals from "./pages/Settings/SensorManuals";
import Support from "./pages/Settings/Support";

import "./i18n";

function App() {
  return (
    <UserRoleProvider>
      <TamaguiProvider config={tamaguiConfig} defaultTheme="dark">
        <ToastProvider>
          <Router>
            <Routes>
              <Route
                path="/login"
                element={
                  <GuestRoute>
                    <Login />
                  </GuestRoute>
                }
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/reset-password-confirmation"
                element={<ResetPasswordConfirmation />}
              />
              <Route path="/oauth-callback" element={<OAuthCallback />} />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Analytics />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Integrations />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sensor-manuals"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SensorManuals />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Support />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/security"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Security />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Notifications />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alerts"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Alerts />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alerts/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <AlertDetails />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/base-stations"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BaseStations />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/base-stations/:hubId/sensors"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Sensors />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/base-stations/:hubId/sensors/:sensorId/events"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SensorEvents />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service-requests"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceRequestsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service-requests/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceRequestDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service-requests/new"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceRequestForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service-requests/edit/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceRequestForm />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CustomerList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/new"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CreateCustomer />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CustomerView />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<RedirectHome />} />
            </Routes>
          </Router>

          <CurrentToast />
          <ToastViewport
            flexDirection="column"
            bottom="$5"
            left="$5"
            right="$5"
          />
        </ToastProvider>
      </TamaguiProvider>
    </UserRoleProvider>
  );
}

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const {
    refreshUserRole,
    role,
    isInitialized: isUserRoleInitialized,
    isRefreshingUserRole,
  } = useUserRoleContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    refreshUserRole?.();
  }, [navigate]);

  useEffect(() => {
    if (isUserRoleInitialized && !isRefreshingUserRole) {
      setIsAuthenticated(!!role && role !== UserRole.UNAUTHENTICATED);
      setIsLoading(false);
    }
  }, [isUserRoleInitialized, isRefreshingUserRole]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Web-admin is an operator tool. A homeowner (UserGroup) account could
  // previously log in and land on the legacy /dashboard page — an outdated
  // customer-facing view nobody maintains. Homeowners belong in the mobile
  // app; block them here instead of showing them stale UI.
  if (role !== UserRole.ADMIN && role !== UserRole.INSTALLER) {
    return <AccessDenied />;
  }

  return children;
};

const AccessDenied = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  return (
    <YStack
      flex={1}
      height="100vh"
      justifyContent="center"
      alignItems="center"
      gap="$4"
      padding="$6">
      <Text fontSize="$9" fontWeight="bold">
        {t("accessDenied.title")}
      </Text>
      <Text fontSize="$4" color="$gray11" textAlign="center" maxWidth={480}>
        {t("accessDenied.description")}
      </Text>
      <Button theme="active" size="$4" onPress={handleSignOut}>
        {t("accessDenied.signOut")}
      </Button>
    </YStack>
  );
};

const GuestRoute = ({ children }: { children: ReactNode }) => {
  const {
    refreshUserRole,
    role,
    isInitialized: isUserRoleInitialized,
    isRefreshingUserRole,
  } = useUserRoleContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    refreshUserRole?.();
  }, [navigate]);

  useEffect(() => {
    if (isUserRoleInitialized && !isRefreshingUserRole) {
      setIsAuthenticated(!!role && role !== UserRole.UNAUTHENTICATED);
      setIsLoading(false);
    }
  }, [isUserRoleInitialized, isRefreshingUserRole]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const RedirectHome = () => {
  const { isInitialized, homeRoute } = useUserRoleContext();

  if (!isInitialized) {
    return <LoadingIndicator />;
  }

  return <Navigate to={homeRoute} replace />;
};

const LoadingIndicator = () => {
  const { t } = useTranslation();

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        fontSize: "18px",
      }}
    >
      {t("common.loading")}
    </div>
  );
};

export default App;
